import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Layout from '@/components/Layout';
import { 
  TrendingUp, 
  TrendingDown,
  Globe, 
  BarChart3, 
  PieChart,
  Building,
  Factory,
  Zap,
  Heart,
  Smartphone,
  Car,
  Home,
  DollarSign,
  Calendar,
  FileText,
  Download,
  Eye,
  Star,
  ArrowUp,
  ArrowDown,
  Minus,
  AlertCircle,
  Info,
  Clock,
  Users,
  Map,
  Settings,
  Target,
  CheckCircle,
  XCircle,
  Edit3,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketOutlook = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [marketData, setMarketData] = useState({});
  const [researchReports, setResearchReports] = useState([]);
  const [sectorAnalysis, setSectorAnalysis] = useState([]);
  const [economicIndicators, setEconomicIndicators] = useState([]);
  const [assetClassViews, setAssetClassViews] = useState([]);
  const [globalEvents, setGlobalEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [thresholds, setThresholds] = useState({
    riskProfile: 15,
    concentration: 10,
    exposure: 20
  });
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    fetchMarketData();
    fetchResearchReports();
    fetchSectorAnalysis();
    fetchEconomicIndicators();
    fetchAssetClassViews();
    fetchGlobalEvents();

    // Auto-refresh every 5 minutes for live data
    const interval = setInterval(() => {
      fetchMarketData();
      fetchSectorAnalysis();
      fetchEconomicIndicators();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const fetchMarketData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/market-data`, config);
      setMarketData(response.data);
    } catch (error) {
      console.error('Error fetching market data:', error);
      toast.error('Failed to fetch market data');
    } finally {
      setLoading(false);
    }
  };

  const fetchResearchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/research-reports`, config);
      setResearchReports(response.data);
    } catch (error) {
      console.error('Error fetching research reports:', error);
    }
  };

  const fetchSectorAnalysis = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/sector-analysis`, config);
      setSectorAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching sector analysis:', error);
    }
  };

  const fetchEconomicIndicators = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/economic-indicators`, config);
      setEconomicIndicators(response.data);
    } catch (error) {
      console.error('Error fetching economic indicators:', error);
    }
  };

  const fetchAssetClassViews = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/asset-class-views`, config);
      setAssetClassViews(response.data);
    } catch (error) {
      console.error('Error fetching asset class views:', error);
    }
  };

  const fetchGlobalEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/global-events`, config);
      setGlobalEvents(response.data);
    } catch (error) {
      console.error('Error fetching global events:', error);
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'BUY': case 'OVERWEIGHT': return <ArrowUp className="h-4 w-4 text-green-600" />;
      case 'SELL': case 'UNDERWEIGHT': return <ArrowDown className="h-4 w-4 text-red-600" />;
      case 'HOLD': case 'NEUTRAL': return <Minus className="h-4 w-4 text-yellow-600" />;
      default: return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'BUY': case 'OVERWEIGHT': return 'text-green-600 bg-green-50 border-green-200';
      case 'SELL': case 'UNDERWEIGHT': return 'text-red-600 bg-red-50 border-red-200';
      case 'HOLD': case 'NEUTRAL': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const updateThreshold = async (type, value) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API}/admin/thresholds`, { type, value }, config);
      toast.success('Threshold updated successfully');
      setEditingThreshold(null);
    } catch (error) {
      console.error('Error updating threshold:', error);
      toast.error('Failed to update threshold');
    }
  };

  const approveRecommendation = async (id, action) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API}/admin/recommendations/${id}`, { action }, config);
      toast.success(`Recommendation ${action}ed successfully`);
      
      // Update local state
      setRecommendations(prev => 
        prev.map(rec => 
          rec.id === id ? { ...rec, status: action === 'accept' ? 'approved' : 'rejected' } : rec
        )
      );
    } catch (error) {
      console.error('Error updating recommendation:', error);
      toast.error('Failed to update recommendation');
    }
  };

  const MarketOverview = () => {
    const indices = marketData?.indices || {};
    const isUS = user?.market === 'US';

    const IndexCard = ({ label, indexKey, locale = 'en-US', fallbackValue }) => {
      const idx = indices[indexKey] || {};
      const hasLive = idx.value != null;
      const change = idx.change ?? 0;
      const changePct = idx.change_percent ?? 0;
      const up = change >= 0;
      const sign = up ? '+' : '';
      return (
        <Card className={`border-l-4 ${up ? 'border-l-green-500' : 'border-l-red-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{label}</p>
                <p className={`text-2xl font-bold ${up ? 'text-green-600' : 'text-red-600'}`}>
                  {hasLive ? idx.value.toLocaleString(locale, { maximumFractionDigits: 2 }) : fallbackValue}
                </p>
                <p className={`text-xs ${up ? 'text-green-600' : 'text-red-600'}`}>
                  {hasLive ? `${sign}${change.toFixed(2)} (${sign}${changePct.toFixed(2)}%)` : '—'}
                </p>
              </div>
              {up ? <TrendingUp className="h-8 w-8 text-green-500" /> : <TrendingDown className="h-8 w-8 text-red-500" />}
            </div>
          </CardContent>
        </Card>
      );
    };

    return (
      <div className="space-y-6">
      {/* Market Summary Cards */}
      {isUS ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <IndexCard label="Dow Jones"  indexKey="dow_jones" fallbackValue="38,500" />
          <IndexCard label="NASDAQ"     indexKey="nasdaq"    fallbackValue="16,200" />
          <IndexCard label="S&P 500"    indexKey="sp500"     fallbackValue="5,100"  />
          <Card className="border-l-4 border-l-ugmonk-yellow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">USD / EUR</p>
                  <p className="text-2xl font-bold text-ugmonk-dark">
                    {indices.usd_eur?.value?.toFixed(4) || '0.9210'}
                  </p>
                  <p className={`text-xs ${(indices.usd_eur?.change ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(indices.usd_eur?.change ?? 0) >= 0 ? '+' : ''}{indices.usd_eur?.change?.toFixed(4) || '0.0000'} ({(indices.usd_eur?.change_percent ?? 0) >= 0 ? '+' : ''}{indices.usd_eur?.change_percent?.toFixed(2) || '0.00'}%)
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-ugmonk-grey" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Gold (USD/oz)</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    ${indices.gold?.value?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '2,625'}
                  </p>
                  <p className={`text-xs ${(indices.gold?.change ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(indices.gold?.change ?? 0) >= 0 ? '+' : ''}{indices.gold?.change?.toFixed(2) || '0.00'} ({(indices.gold?.change_percent ?? 0) >= 0 ? '+' : ''}{indices.gold?.change_percent?.toFixed(2) || '0.00'}%)
                  </p>
                </div>
                <Star className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <IndexCard label="Nifty 50" indexKey="nifty50" locale="en-IN" fallbackValue="24,456" />
          <IndexCard label="Sensex"   indexKey="sensex"  locale="en-IN" fallbackValue="80,234" />
          <Card className="border-l-4 border-l-ugmonk-yellow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">USD / INR</p>
                  <p className="text-2xl font-bold text-ugmonk-dark">
                    {indices.usd_inr?.value || '83.45'}
                  </p>
                  <p className={`text-xs ${(indices.usd_inr?.change ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(indices.usd_inr?.change ?? 0) >= 0 ? '+' : ''}{indices.usd_inr?.change || '+0.12'} ({(indices.usd_inr?.change_percent ?? 0) >= 0 ? '+' : ''}{indices.usd_inr?.change_percent || '+0.14'}%)
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-ugmonk-grey" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Gold (₹/10g)</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    ₹{indices.gold?.value?.toLocaleString('en-IN') || '1,27,000'}
                  </p>
                  <p className={`text-xs ${(indices.gold?.change ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(indices.gold?.change ?? 0) >= 0 ? '+' : ''}{indices.gold?.change || '+234'} ({(indices.gold?.change_percent ?? 0) >= 0 ? '+' : ''}{indices.gold?.change_percent || '+0.36'}%)
                  </p>
                </div>
                <Star className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Market Commentary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-ugmonk-grey" />
            Market Commentary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-br from-[#fffdf0] to-[#f8f6f1] rounded-lg p-6 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-bold text-lg text-gray-900">Weekly Market Outlook - {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
              {marketData?.last_updated && (
                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                  Updated: {new Date(marketData.last_updated).toLocaleString('en-IN')}
                </span>
              )}
            </div>
            {isUS ? (<>
              <p className="text-gray-700 mb-3">
                <strong>Key Highlights:</strong> US markets showed resilience this week led by Technology and Industrials.
                AI-driven capex cycle continues to support mega-cap earnings while energy and real estate lag.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Macro Context:</strong> Fed signals a data-dependent pause; core PCE remains sticky above 2% target.
                Strong labor market supports consumer spending but elevates higher-for-longer rate risk.
              </p>
              <p className="text-gray-700">
                <strong>Outlook:</strong> We maintain overweight US large-cap equities and investment-grade bonds.
                Selective exposure to AI infrastructure and healthcare innovation themes.
              </p>
            </>) : (<>
              <p className="text-gray-700 mb-3">
                <strong>Key Highlights:</strong> Indian markets showed mixed performance this week amid global uncertainty.
                IT sector outperformed on strong Q3 results while banking stocks faced pressure from RBI policy concerns.
              </p>
              <p className="text-gray-700 mb-3">
                <strong>Global Context:</strong> Fed dovish stance supports emerging market flows. Crude oil volatility
                remains a key monitorable for Indian markets given geopolitical tensions.
              </p>
              <p className="text-gray-700">
                <strong>Outlook:</strong> We maintain cautiously optimistic stance with focus on quality large-caps
                and defensive positioning ahead of budget announcements.
              </p>
            </>)}
          </div>

          <div className="flex gap-4 text-sm">
            {isUS ? (<>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full"></div><span>Bullish: Technology, Industrials</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full"></div><span>Neutral: Financials, Consumer</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full"></div><span>Bearish: Energy, REITs</span></div>
            </>) : (<>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-500 rounded-full"></div><span>Bullish: IT, Pharma</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-yellow-500 rounded-full"></div><span>Neutral: FMCG, Auto</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full"></div><span>Bearish: Banking, Metals</span></div>
            </>)}
          </div>
        </CardContent>
      </Card>

      {/* Global Events Impact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5 text-ugmonk-grey" />
            Global Events & Market Impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              {
                event: "US Fed Rate Decision",
                impact: "Positive",
                description: "Fed signals dovish stance, supporting EM flows to India",
                date: "Nov 20, 2025",
                severity: "High"
              },
              {
                event: "China PMI Data",
                impact: "Mixed",
                description: "Manufacturing PMI at 49.8, services remain robust at 52.1",
                date: "Nov 18, 2025",
                severity: "Medium"
              },
              {
                event: "Crude Oil Volatility",
                impact: "Negative",
                description: "Geopolitical tensions push Brent above $85, impacting import costs",
                date: "Nov 15, 2025",
                severity: "High"
              }
            ].map((event, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                event.impact === 'Positive' ? 'border-green-500 bg-green-50' :
                event.impact === 'Negative' ? 'border-red-500 bg-red-50' :
                'border-yellow-500 bg-yellow-50'
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{event.event}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        event.impact === 'Positive' ? 'bg-green-200 text-green-800' :
                        event.impact === 'Negative' ? 'bg-red-200 text-red-800' :
                        'bg-yellow-200 text-yellow-800'
                      }`}>
                        {event.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{event.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{event.date}</p>
                    <p className={`text-xs font-medium ${
                      event.severity === 'High' ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {event.severity} Impact
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </div>
    );
  };

  const SectorAnalysis = () => (
    <div className="space-y-6">
      {/* Sector Performance Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { name: 'Information Technology', recommendation: 'OVERWEIGHT', performance: '+12.4%', icon: Smartphone, rationale: 'Strong Q3 earnings, AI adoption driving growth' },
          { name: 'Pharmaceuticals', recommendation: 'OVERWEIGHT', performance: '+8.7%', icon: Heart, rationale: 'Robust export demand, new product launches' },
          { name: 'Banking & Financial', recommendation: 'UNDERWEIGHT', performance: '-3.2%', icon: Building, rationale: 'NPA concerns, margin pressure from rate cuts' },
          { name: 'Automotive', recommendation: 'NEUTRAL', performance: '+2.1%', icon: Car, rationale: 'Mixed demand, EV transition challenges' },
          { name: 'FMCG', recommendation: 'NEUTRAL', performance: '+1.8%', icon: Home, rationale: 'Stable demand, rural recovery slow' },
          { name: 'Metals & Mining', recommendation: 'UNDERWEIGHT', performance: '-5.6%', icon: Factory, rationale: 'China demand concerns, oversupply issues' }
        ].map((sector, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <sector.icon className="h-5 w-5 text-ugmonk-dark" />
                  <h3 className="font-semibold text-sm text-gray-900">{sector.name}</h3>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  getRecommendationColor(sector.recommendation)
                }`}>
                  {getRecommendationIcon(sector.recommendation)}
                  {sector.recommendation}
                </div>
              </div>
              
              <div className="mb-3">
                <div className={`text-lg font-bold ${
                  sector.performance.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {sector.performance}
                </div>
                <div className="text-xs text-gray-500">YTD Performance</div>
              </div>
              
              <p className="text-xs text-gray-600 line-clamp-2">{sector.rationale}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Sector Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Sector Analysis & Top Picks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {[
              {
                sector: 'Information Technology',
                recommendation: 'OVERWEIGHT',
                topPicks: ['TCS', 'Infosys', 'HCL Tech'],
                targetAllocation: '18-20%',
                keyDrivers: ['AI/ML adoption', 'Digital transformation', 'Strong deal pipeline'],
                risks: ['Client budget cuts', 'Visa restrictions'],
                outlook: 'Strong revenue growth expected in FY25 driven by generative AI investments and digital transformation projects.'
              },
              {
                sector: 'Pharmaceuticals',
                recommendation: 'OVERWEIGHT', 
                topPicks: ['Sun Pharma', 'Dr. Reddy\'s', 'Cipla'],
                targetAllocation: '12-15%',
                keyDrivers: ['US generic opportunities', 'Biosimilar launches', 'Domestic pricing power'],
                risks: ['FDA inspections', 'Patent challenges'],
                outlook: 'Favorable regulatory environment and new product launches to drive growth momentum.'
              },
              {
                sector: 'Banking & Financial',
                recommendation: 'UNDERWEIGHT',
                topPicks: ['HDFC Bank', 'ICICI Bank', 'Kotak Mahindra'],
                targetAllocation: '12-14%',
                keyDrivers: ['Credit growth recovery', 'Digital banking', 'Fee income growth'],
                risks: ['Asset quality stress', 'Margin compression', 'Regulatory overhang'],
                outlook: 'Cautious on near-term profitability due to competitive pressures and credit costs.'
              }
            ].map((analysis, index) => (
              <div key={index} className="border rounded-lg p-5">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{analysis.sector}</h3>
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                      getRecommendationColor(analysis.recommendation)
                    }`}>
                      {getRecommendationIcon(analysis.recommendation)}
                      {analysis.recommendation}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Target Allocation</p>
                    <p className="text-lg font-bold text-ugmonk-dark">{analysis.targetAllocation}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Top Picks</h4>
                    <div className="flex flex-wrap gap-1">
                      {analysis.topPicks.map((stock, i) => (
                        <span key={i} className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded">
                          {stock}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Key Drivers</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {analysis.keyDrivers.map((driver, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                          {driver}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Key Risks</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {analysis.risks.map((risk, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <div className="w-1 h-1 bg-red-500 rounded-full"></div>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-1">Investment Outlook</h4>
                  <p className="text-sm text-gray-700">{analysis.outlook}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const EconomicOutlook = () => (
    <div className="space-y-6">
      {/* Economic Indicators Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { name: 'GDP Growth', value: '6.8%', change: '+0.2%', status: 'positive', icon: BarChart3 },
          { name: 'Inflation (CPI)', value: '4.2%', change: '-0.3%', status: 'positive', icon: TrendingDown },
          { name: 'Repo Rate', value: '6.50%', change: '0%', status: 'neutral', icon: DollarSign },
          { name: 'Fiscal Deficit', value: '5.8%', change: '+0.1%', status: 'negative', icon: AlertCircle }
        ].map((indicator, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <indicator.icon className={`h-5 w-5 ${
                  indicator.status === 'positive' ? 'text-green-500' :
                  indicator.status === 'negative' ? 'text-red-500' : 'text-gray-500'
                }`} />
                <span className={`text-xs px-2 py-1 rounded-full ${
                  indicator.status === 'positive' ? 'bg-green-100 text-green-700' :
                  indicator.status === 'negative' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {indicator.change}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">{indicator.name}</p>
                <p className="text-2xl font-bold text-gray-900">{indicator.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Economic Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-ugmonk-grey" />
            Economic Analysis & Projections
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* GDP & Growth */}
            <div className="border rounded-lg p-4">
              <h3 className="font-bold text-lg text-gray-900 mb-3">GDP Growth Outlook</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Current Quarter (Q2 FY25):</strong> GDP growth at 6.8% exceeded expectations, 
                    driven by robust services sector and government capex. Manufacturing recovery remains gradual.
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>FY25 Projection:</strong> We maintain our GDP growth forecast at 6.5-7.0% 
                    with risks tilted to the downside due to global uncertainties.
                  </p>
                </div>
                <div className="bg-[#fffdf0] p-4 rounded-lg">
                  <h4 className="font-semibold text-ugmonk-darker mb-2">Key Growth Drivers</h4>
                  <ul className="text-sm text-ugmonk-darker space-y-1">
                    <li>• Government infrastructure spending</li>
                    <li>• Services sector resilience</li>
                    <li>• Rural demand recovery</li>
                    <li>• Export competitiveness</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Inflation & Monetary Policy */}
            <div className="border rounded-lg p-4">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Inflation & Monetary Policy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>Current Inflation:</strong> CPI inflation moderated to 4.2% in October, 
                    within RBI's comfort zone. Food inflation remains volatile due to seasonal factors.
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>RBI Policy Stance:</strong> We expect RBI to maintain accommodative stance 
                    with potential 25bps cut in Q4 FY25 if inflation remains benign.
                  </p>
                </div>
                <div className="bg-[#fffdf0] p-4 rounded-lg">
                  <h4 className="font-semibold text-ugmonk-darker mb-2">Inflation Risks</h4>
                  <ul className="text-sm text-ugmonk-darker space-y-1">
                    <li>• Crude oil price volatility</li>
                    <li>• Monsoon dependency</li>
                    <li>• Global supply chain disruptions</li>
                    <li>• Geopolitical tensions</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Currency & External Sector */}
            <div className="border rounded-lg p-4">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Currency & External Sector</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">USD/INR Outlook</h4>
                  <p className="text-sm text-gray-700">
                    Rupee likely to trade in 82.50-84.50 range. RBI intervention and strong FII flows 
                    provide support against global dollar strength.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Current Account</h4>
                  <p className="text-sm text-gray-700">
                    CAD expected to moderate to 1.8% of GDP in FY25 from 2.1% in FY24, 
                    aided by services exports and lower commodity imports.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">FX Reserves</h4>
                  <p className="text-sm text-gray-700">
                    Forex reserves at $575bn provide adequate buffer. Import cover 
                    of 10+ months offers stability against external shocks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Market Implications */}
      <Card>
        <CardHeader>
          <CardTitle>Investment Implications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-bold text-green-700">Positive Factors</h3>
              {[
                'Stable GDP growth trajectory supports earnings',
                'Benign inflation outlook enables policy easing',
                'Strong fiscal position allows counter-cyclical spending',
                'Robust foreign exchange reserves provide stability'
              ].map((factor, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700">{factor}</p>
                </div>
              ))}
            </div>
            
            <div className="space-y-4">
              <h3 className="font-bold text-red-700">Risk Factors</h3>
              {[
                'Global growth slowdown impacts export demand',
                'Monsoon failure could trigger food inflation',
                'Geopolitical tensions affect commodity prices',
                'US Fed policy changes impact capital flows'
              ].map((risk, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700">{risk}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ResearchReports = () => (
    <div className="space-y-6">
      {/* Latest Research Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-ugmonk-grey" />
            Latest Research Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              {
                title: 'Q3 FY25 Earnings Preview: IT Sector',
                type: 'Sector Report',
                date: 'Nov 22, 2025',
                analyst: 'Priya Sharma',
                summary: 'Expect strong Q3 performance driven by AI/ML project ramp-ups and deal conversions. Margin expansion likely.',
                recommendation: 'OVERWEIGHT',
                priority: 'High'
              },
              {
                title: 'Reliance Industries: Retail Renaissance',
                type: 'Company Report',
                date: 'Nov 20, 2025',
                analyst: 'Rajesh Kumar',
                summary: 'Retail business showing strong recovery post-pandemic. Digital commerce integration driving growth.',
                recommendation: 'BUY',
                priority: 'High'
              },
              {
                title: 'Banking Sector: NPA Cycle Bottoming Out',
                type: 'Sector Report',
                date: 'Nov 18, 2025',
                analyst: 'Meera Patel',
                summary: 'Credit costs expected to normalize. PSU banks showing improvement in asset quality metrics.',
                recommendation: 'NEUTRAL',
                priority: 'Medium'
              },
              {
                title: 'Infrastructure Theme: Policy Push Continues',
                type: 'Thematic Report',
                date: 'Nov 15, 2025',
                analyst: 'Vikram Singh',
                summary: 'Government capex allocation and PLI schemes to benefit infrastructure and manufacturing sectors.',
                recommendation: 'OVERWEIGHT',
                priority: 'Medium'
              }
            ].map((report, index) => (
              <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{report.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        report.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {report.priority}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded">{report.type}</span>
                      <span>By {report.analyst}</span>
                      <span>{report.date}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{report.summary}</p>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      getRecommendationColor(report.recommendation)
                    }`}>
                      {getRecommendationIcon(report.recommendation)}
                      {report.recommendation}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Asset Class Views */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5 text-green-500" />
            Asset Class Views & Allocation Guidance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                assetClass: 'Indian Equities',
                recommendation: 'OVERWEIGHT',
                allocation: '55-60%',
                rationale: 'Domestic growth drivers intact, earnings recovery expected',
                timeHorizon: '12-18 months'
              },
              {
                assetClass: 'Fixed Income',
                recommendation: 'NEUTRAL',
                allocation: '25-30%',
                rationale: 'Rate cycle peaking, duration risk moderating',
                timeHorizon: '6-12 months'
              },
              {
                assetClass: 'International Equities',
                recommendation: 'UNDERWEIGHT',
                allocation: '5-10%',
                rationale: 'Global growth concerns, strong dollar headwinds',
                timeHorizon: '12-24 months'
              },
              {
                assetClass: 'Gold',
                recommendation: 'NEUTRAL',
                allocation: '5-8%',
                rationale: 'Hedge against currency and geopolitical risks',
                timeHorizon: '6-18 months'
              },
              {
                assetClass: 'Real Estate/REITs',
                recommendation: 'OVERWEIGHT',
                allocation: '3-5%',
                rationale: 'Commercial real estate recovery, yield advantage',
                timeHorizon: '18-36 months'
              },
              {
                assetClass: 'Alternative Investments',
                recommendation: 'NEUTRAL',
                allocation: '2-5%',
                rationale: 'Selective opportunities in PE/VC space',
                timeHorizon: '24-60 months'
              }
            ].map((view, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-900">{view.assetClass}</h3>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      getRecommendationColor(view.recommendation)
                    }`}>
                      {getRecommendationIcon(view.recommendation)}
                      {view.recommendation}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-600">Target Allocation</p>
                      <p className="text-lg font-bold text-ugmonk-dark">{view.allocation}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600">Investment Horizon</p>
                      <p className="text-sm font-medium text-gray-900">{view.timeHorizon}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600">Rationale</p>
                      <p className="text-xs text-gray-700 line-clamp-2">{view.rationale}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Investment Themes */}
      <Card>
        <CardHeader>
          <CardTitle>Key Investment Themes for 2025</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                theme: 'Digital Transformation & AI',
                sectors: ['IT Services', 'Fintech', 'Telecom'],
                description: 'Accelerated adoption of AI/ML technologies across industries driving demand for digital services and infrastructure.',
                investmentOpportunities: ['Large IT services companies', 'Cloud infrastructure', 'Cybersecurity firms'],
                riskLevel: 'Medium'
              },
              {
                theme: 'Green Energy Transition',
                sectors: ['Renewable Energy', 'EV/Auto', 'Power'],
                description: 'Government push for renewable energy and EV adoption creating long-term investment opportunities.',
                investmentOpportunities: ['Solar/Wind developers', 'EV manufacturers', 'Battery technology'],
                riskLevel: 'High'
              },
              {
                theme: 'Manufacturing Renaissance',
                sectors: ['Capital Goods', 'Chemicals', 'Electronics'],
                description: 'PLI schemes and China+1 strategy benefiting domestic manufacturing across multiple sectors.',
                investmentOpportunities: ['Engineering companies', 'Specialty chemicals', 'Electronic components'],
                riskLevel: 'Medium'
              },
              {
                theme: 'Financial Inclusion',
                sectors: ['Banking', 'Insurance', 'Fintech'],
                description: 'Rising income levels and digital penetration expanding formal financial services reach.',
                investmentOpportunities: ['Private banks', 'Life insurance', 'Digital lending platforms'],
                riskLevel: 'Low'
              }
            ].map((theme, index) => (
              <div key={index} className="border rounded-lg p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-lg text-gray-900">{theme.theme}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    theme.riskLevel === 'High' ? 'bg-red-100 text-red-700' :
                    theme.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {theme.riskLevel} Risk
                  </span>
                </div>
                
                <p className="text-sm text-gray-700 mb-3">{theme.description}</p>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-gray-900 mb-1">Key Sectors</p>
                    <div className="flex flex-wrap gap-1">
                      {theme.sectors.map((sector, i) => (
                        <span key={i} className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded">
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-gray-900 mb-1">Investment Opportunities</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {theme.investmentOpportunities.map((opp, i) => (
                        <li key={i} className="flex items-center gap-1">
                          <div className="w-1 h-1 bg-green-500 rounded-full"></div>
                          {opp}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ThresholdSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Deviation Thresholds
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid gap-6">
              {/* Risk Profile Threshold */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Risk Profile Deviations</h3>
                  <p className="text-sm text-gray-600">Threshold for flagging risk profile misalignments</p>
                </div>
                <div className="flex items-center gap-2">
                  {editingThreshold === 'riskProfile' ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={thresholds.riskProfile}
                        onChange={(e) => setThresholds(prev => ({ ...prev, riskProfile: e.target.value }))}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                      <Button size="sm" onClick={() => updateThreshold('riskProfile', thresholds.riskProfile)}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{thresholds.riskProfile}%</span>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold('riskProfile')}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Concentration Threshold */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Concentration Deviations</h3>
                  <p className="text-sm text-gray-600">Threshold for flagging over-concentration in securities</p>
                </div>
                <div className="flex items-center gap-2">
                  {editingThreshold === 'concentration' ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={thresholds.concentration}
                        onChange={(e) => setThresholds(prev => ({ ...prev, concentration: e.target.value }))}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                      <Button size="sm" onClick={() => updateThreshold('concentration', thresholds.concentration)}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{thresholds.concentration}%</span>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold('concentration')}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Exposure Threshold */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Exposure Deviations</h3>
                  <p className="text-sm text-gray-600">Threshold for flagging sector/asset class exposure limits</p>
                </div>
                <div className="flex items-center gap-2">
                  {editingThreshold === 'exposure' ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={thresholds.exposure}
                        onChange={(e) => setThresholds(prev => ({ ...prev, exposure: e.target.value }))}
                        className="w-20"
                      />
                      <span className="text-sm">%</span>
                      <Button size="sm" onClick={() => updateThreshold('exposure', thresholds.exposure)}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{thresholds.exposure}%</span>
                      <Button size="sm" variant="outline" onClick={() => setEditingThreshold('exposure')}>
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const RecommendationReview = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Recommendation Review
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recommendations.map((rec) => (
              <div key={rec.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{rec.client_name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        rec.status === 'pending' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                        rec.status === 'approved' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {rec.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{rec.description}</p>
                    
                    <div className="bg-[#fffdf0] p-3 rounded-lg mb-3">
                      <p className="text-sm font-medium text-ugmonk-darker">Recommended Action:</p>
                      <p className="text-sm text-ugmonk-dark mt-1">{rec.recommendation}</p>
                    </div>

                    {rec.notes && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-800">Notes:</p>
                        <p className="text-sm text-gray-700 mt-1">{rec.notes}</p>
                      </div>
                    )}
                  </div>

                  {rec.status === 'pending' && (
                    <div className="flex flex-col gap-2 ml-4">
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => approveRecommendation(rec.id, 'accept')}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => approveRecommendation(rec.id, 'modify')}
                      >
                        <Edit3 className="h-4 w-4 mr-1" />
                        Modify
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => approveRecommendation(rec.id, 'reject')}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Market Overview', icon: Globe, component: MarketOverview },
    { id: 'sectors', label: 'Sector Analysis', icon: Building, component: SectorAnalysis },
    { id: 'economic', label: 'Economic Outlook', icon: BarChart3, component: EconomicOutlook },
    { id: 'research', label: 'Research Reports', icon: FileText, component: ResearchReports },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || MarketOverview;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-ugmonk-dark" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Market Outlook & Research</h1>
                <p className="text-gray-600">
                  Comprehensive market analysis, sector insights, economic outlook, and investment research
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {loading && (
                <div className="w-6 h-6 border-2 border-ugmonk-yellow border-t-transparent rounded-full animate-spin"></div>
              )}
              <Button 
                onClick={() => {
                  fetchMarketData();
                  fetchResearchReports();
                  fetchSectorAnalysis();
                  fetchEconomicIndicators();
                  fetchAssetClassViews();
                  fetchGlobalEvents();
                }}
                variant="outline"
                size="sm"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Clock className="h-4 w-4" />
                Refresh Data
              </Button>
              {user?.role === 'rm' && (
                <span className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded-full font-medium">
                  Read Only
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div>
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-ugmonk-yellow text-ugmonk-dark'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Active Tab Content */}
        <ActiveComponent />
      </div>
    </Layout>
  );
};

export default MarketOutlook;